/**
 * Recovery for Vite dynamic-import / preload failures after a deployment.
 *
 * Why: a new build replaces every `assets/[name]-[hash].js` chunk. A tab loaded before the deploy
 * still holds the OLD chunk URL in memory; the next lazy navigation fails ("Failed to fetch
 * dynamically imported module"). The cure is a single page reload so the new chunk URLs are used.
 *
 * Two call sites share the rate-limit so neither double-reloads:
 * - `src/main.tsx` — `window.addEventListener("vite:preloadError", …)` (fires before React renders)
 * - `src/components/RouteErrorBoundary.tsx` — fallback when the error bubbles past Vite's event
 */

export const CHUNK_RECOVERY_KEY = "oktogo_chunk_recovery_attempt";
/** Avoid infinite reload loops if the chunk really is unavailable (bad deploy / offline). */
const CHUNK_RECOVERY_WINDOW_MS = 30_000;

type RecoveryAttempt = { path: string; timestamp: number };

function readLastRecoveryAttempt(): RecoveryAttempt | null {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RECOVERY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RecoveryAttempt>;
    if (typeof parsed.path !== "string" || typeof parsed.timestamp !== "number") return null;
    return { path: parsed.path, timestamp: parsed.timestamp };
  } catch {
    return null;
  }
}

function writeLastRecoveryAttempt(attempt: RecoveryAttempt): void {
  try {
    window.sessionStorage.setItem(CHUNK_RECOVERY_KEY, JSON.stringify(attempt));
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

/** Drop the rate-limit marker once the page renders successfully so unrelated future failures aren't blocked. */
export function clearChunkRecoveryMarker(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
  } catch {
    // Ignore storage failures.
  }
}

/** True if we should attempt a reload for the current pathname (records the attempt as a side-effect). */
export function shouldAttemptChunkRecovery(): boolean {
  const lastAttempt = readLastRecoveryAttempt();
  const now = Date.now();
  if (
    lastAttempt &&
    lastAttempt.path === window.location.pathname &&
    now - lastAttempt.timestamp <= CHUNK_RECOVERY_WINDOW_MS
  ) {
    return false;
  }
  writeLastRecoveryAttempt({ path: window.location.pathname, timestamp: now });
  return true;
}

/** Recognize chunk-load failure phrasings across Chromium / Firefox / Safari. */
export function isChunkLoadError(error: unknown): boolean {
  const message = chunkErrorMessage(error);
  if (!message) return false;
  const lowered = message.toLowerCase();
  return (
    lowered.includes("failed to fetch dynamically imported module") ||
    lowered.includes("importing a module script failed") ||
    lowered.includes("loading chunk") ||
    lowered.includes("loading css chunk") ||
    // Safari: "Unable to preload CSS for <url>" / "error loading dynamically imported module"
    lowered.includes("error loading dynamically imported module") ||
    lowered.includes("unable to preload")
  );
}

function chunkErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return null;
}
